import React from "react";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import { KvRow } from "kontrolEdge/components/Sdwan/shared/sdwanFormat";
import { formatUptime, tunnelTraffic, TunnelStatus, type Tunnel, type Tunnels } from "../tunnels/tunnels";
import type { FirewallEvents } from "../events/events.types";
import type { FirewallNat } from "../nat/nat.types";
import type { FirewallPolicies, FirewallPolicy } from "../policies/policies.types";
import type { Reading } from "../readings/useFwaasReading";
import { NO_READING } from "../overview/overviewFormat";
import { connectedServiceLabel } from "../connectionPath";
import { dialUpNames, type AttentionItem } from "../attention/needsAttention";
import { SEVERITY_TAG } from "../attention/NeedsAttentionCard";
import { LANE_LABEL, drawnIds, type Lane, type MapNode, type NodeIcon } from "./serviceMap";
import { eventsOfNode, publishedApps, publishingRules, siteToSiteNames, tunnelRules, type PublishedApp } from "./nodeDetails";
import {
    ConnectionLinks,
    EventList,
    InspectorAttention,
    InspectorHead,
    InspectorRow,
    InspectorRows,
    InspectorSection,
    RuleList,
} from "./MapInspector";

const KICKER: Record<NodeIcon, string> = {
    internet: "Service connection · IPX",
    published: "Published apps · NAT",
    remote: "Remote access · IPsec dial-up",
    "site-to-site": "Site-to-site VPN · IPsec",
    fwint: "Private connection",
    cloud: "Service connection · CC",
    other: "Service connection",
};

/** The readings the inspectors draw on: the page's, never a request of their own. */
export interface NodeReadings {
    tunnels: Tunnels | null;
    nat: Reading<FirewallNat>;
    policies: Reading<FirewallPolicies>;
    events: Reading<FirewallEvents>;
    /** A rule's name shows that rule in the Policies table. */
    onRuleClick: (ruleId: number) => void;
}

interface BodyProps {
    node: MapNode;
    nodes: MapNode[];
    readings: NodeReadings;
    onSelect: (id: string) => void;
}

const compactCount = new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 });
const hitsLabel = (hits: number) => `${compactCount.format(hits)} ${hits === 1 ? "hit" : "hits"}`;
const ruleHits = (rules: { hits: number | null }[]) => rules.reduce((sum, rule) => sum + (rule.hits ?? 0), 0);

type SiteToSiteTunnel = Extract<Tunnel, { kind: "site-to-site" }>;
type DialUpTunnel = Extract<Tunnel, { kind: "dial-up" }>;
const isSiteToSite = (t: Tunnel): t is SiteToSiteTunnel => t.kind === "site-to-site";
const isDialUp = (t: Tunnel): t is DialUpTunnel => t.kind === "dial-up";

/** "2 · 6.3k hits", "None", or the state of a reading not in yet. */
function inboundRulesLabel(rules: Reading<FirewallPolicy[]>): string {
    if (rules === undefined) return "Reading…";
    if (rules === null) return NO_READING;
    if (rules.length === 0) return "None";
    return `${rules.length} · ${hitsLabel(ruleHits(rules))}`;
}

/** "2 phase 2 · up 3d 4h · ↓ 1.2 MB · ↑ 300 KB" — what is known of a site-to-site tunnel besides its state. */
const siteToSiteDetail = (tunnel: SiteToSiteTunnel) =>
    [
        tunnel.phase2.length ? `${tunnel.phase2.length} phase 2` : null,
        tunnel.uptime_seconds != null ? `up ${formatUptime(tunnel.uptime_seconds)}` : null,
        tunnelTraffic(tunnel),
    ]
        .filter(Boolean)
        .join(" · ");

/** "FortiClient · ↓ 6.8 MB · ↑ 22.2 MB". */
const dialUpDetail = (tunnel: DialUpTunnel) => [tunnel.forticlient ? "FortiClient" : "Dial-up", tunnelTraffic(tunnel)].filter(Boolean).join(" · ");

/** A link to another node of the map, by name — only when that node is drawn. */
function NodeLinks({ ids, nodes, onSelect }: { ids: string[]; nodes: MapNode[]; onSelect: (id: string) => void }) {
    const drawn = ids.map((id) => nodes.find((n) => n.id === id)).filter((n): n is MapNode => Boolean(n));
    if (drawn.length === 0) return null;
    return (
        <>
            {drawn.map((node, i) => (
                <React.Fragment key={node.id}>
                    {i > 0 ? ", " : null}
                    <button type="button" onClick={() => onSelect(node.id)} className="font-medium text-semantic-text-brand hover:underline">
                        {node.name}
                    </button>
                </React.Fragment>
            ))}
        </>
    );
}

/** Internet: what rides on it, and the rules that let the internet in to published apps. */
function InternetBody({ node, nodes, readings, onSelect }: BodyProps) {
    const rules = publishingRules(readings.nat, readings.policies);
    const carries = <NodeLinks ids={["published", "remote"]} nodes={nodes} onSelect={onSelect} />;
    return (
        <>
            <InspectorSection label="On the firewall">
                {nodes.some((n) => n.id === "published" || n.id === "remote") && <KvRow label="Carries">{carries}</KvRow>}
                <KvRow label="Inbound rules">{inboundRulesLabel(rules)}</KvRow>
            </InspectorSection>
            <ConnectionLinks node={node} />
        </>
    );
}

/** One published app: its name, protocol and port, and the hits of the rules that let it in. */
function PublishedAppRow({ app }: { app: PublishedApp }) {
    return (
        <InspectorRow
            title={
                <span className={`inline-flex flex-wrap items-center gap-1.5 ${app.enabled ? "" : "text-semantic-text-secondary"}`}>
                    {app.name ?? <span className="italic">Hidden name</span>}
                    {!app.enabled && <GenericTag label="Off" variant="neutral" size="small" />}
                </span>
            }
            detail={`${app.protocol ?? "Any protocol"} · ${app.ports}`}
            aside={
                app.hits == null ? (
                    <span className="text-semantic-text-secondary" title="No rule names this app directly">
                        —
                    </span>
                ) : (
                    <span className="tabular-nums whitespace-nowrap" title="Hits of the rules that publish it; apps behind the same rule share them">
                        {hitsLabel(app.hits)}
                    </span>
                )
            }
        />
    );
}

/** Published apps: each app with its ports and the hits of the rules that publish it. */
function PublishedBody({ nodes, readings, onSelect }: BodyProps) {
    const nat = readings.nat;
    // The node is only drawn once the apps are read; this guards a reading that went away.
    if (!nat) return <p className="text-sm text-semantic-text-secondary">{NO_READING}</p>;
    return (
        <>
            <InspectorRows>
                {publishedApps(nat, readings.policies).map((app) => (
                    <PublishedAppRow key={`${app.name}|${app.protocol}|${app.ports}`} app={app} />
                ))}
            </InspectorRows>
            {nodes.some((n) => n.id === "internet") && (
                <KvRow label="Enters through">
                    <NodeLinks ids={["internet"]} nodes={nodes} onSelect={onSelect} />
                </KvRow>
            )}
            <InspectorSection label="Rules that publish them">
                <RuleList rules={publishingRules(nat, readings.policies)} empty="No rule names these apps directly." onRuleClick={readings.onRuleClick} />
            </InspectorSection>
        </>
    );
}

/** Remote access: who is connected, the tunnel's events and its rules. */
function RemoteBody({ nodes, readings }: BodyProps) {
    const names = dialUpNames(readings.tunnels);
    const dialUps = (readings.tunnels?.items ?? []).filter(isDialUp);
    return (
        <>
            <InspectorSection label={dialUps.length === 1 ? "Tunnel" : "Tunnels"}>
                <InspectorRows>
                    {dialUps.map((tunnel) => (
                        <InspectorRow
                            key={tunnel.name}
                            title={tunnel.name}
                            detail={dialUpDetail(tunnel)}
                            aside={<TunnelStatus tunnel={tunnel} />}
                        />
                    ))}
                </InspectorRows>
            </InspectorSection>
            <InspectorSection label="Events">
                <EventList events={eventsOfNode(readings.events, readings.tunnels, "remote", drawnIds(nodes))} empty="Nothing logged recently." />
            </InspectorSection>
            <InspectorSection label="Rules for this tunnel">
                <RuleList rules={tunnelRules(readings.policies, names)} empty="No rule uses this tunnel." onRuleClick={readings.onRuleClick} />
            </InspectorSection>
        </>
    );
}

/** Site-to-site: each tunnel with its state and phase 2s, its events and its rules. */
function SiteToSiteBody({ nodes, readings }: BodyProps) {
    const tunnels = (readings.tunnels?.items ?? []).filter(isSiteToSite);
    return (
        <>
            <InspectorSection label="Tunnels">
                <InspectorRows>
                    {tunnels.map((tunnel) => (
                        <InspectorRow
                            key={tunnel.name}
                            title={tunnel.name}
                            detail={siteToSiteDetail(tunnel) || undefined}
                            aside={<TunnelStatus tunnel={tunnel} />}
                        />
                    ))}
                </InspectorRows>
            </InspectorSection>
            <InspectorSection label="Events">
                <EventList events={eventsOfNode(readings.events, readings.tunnels, "site-to-site", drawnIds(nodes))} empty="Nothing logged recently." />
            </InspectorSection>
            <InspectorSection label="Rules for these tunnels">
                <RuleList rules={tunnelRules(readings.policies, siteToSiteNames(readings.tunnels))} empty="No rule uses these tunnels." onRuleClick={readings.onRuleClick} />
            </InspectorSection>
        </>
    );
}

/**
 * An interface, the cloud or another connection: the service it is and where it leads.
 * Its datacenter, VLAN and BGP session come with CTX-1210; until then they are not shown,
 * rather than guessed.
 */
function ConnectionBody({ node }: BodyProps) {
    const single = node.connections.length === 1 ? node.connections[0] : null;
    const ridesOn = single ? connectedServiceLabel(single) : null;
    return (
        <>
            {single && (
                <InspectorSection label="Connection">
                    <KvRow label="Service ID">{single.subscriptionId || single.id}</KvRow>
                    {single.connectedServiceDatacenter && <KvRow label="Datacenter">{single.connectedServiceDatacenter}</KvRow>}
                    {ridesOn && <KvRow label="Rides on">{ridesOn}</KvRow>}
                </InspectorSection>
            )}
            <ConnectionLinks node={node} />
        </>
    );
}

/** Inbound IPsec attempts with no node of their own still show, on Internet. */
function InternetEvents({ nodes, readings }: { nodes: MapNode[]; readings: NodeReadings }) {
    const events = eventsOfNode(readings.events, readings.tunnels, "internet", drawnIds(nodes));
    if (!events || events.length === 0) return null;
    return (
        <InspectorSection label="Inbound IPsec attempts">
            <EventList events={events} empty="" />
        </InspectorSection>
    );
}

const BODY: Record<NodeIcon, (props: BodyProps) => JSX.Element> = {
    internet: InternetBody,
    published: PublishedBody,
    remote: RemoteBody,
    "site-to-site": SiteToSiteBody,
    fwint: ConnectionBody,
    cloud: ConnectionBody,
    other: ConnectionBody,
};

/**
 * A node's inspector (CTX-1264): what it is, what needs attention about it, and its own
 * part of the firewall — the rules, events, apps and tunnels that belong to it. Rows lead
 * to other nodes or, with "View connection", to the connection's page.
 */
export function NodeInspector({
    node,
    nodes,
    readings,
    attention,
    titleRef,
    onSelect,
    onClose,
}: {
    node: MapNode;
    nodes: MapNode[];
    readings: NodeReadings;
    attention: AttentionItem[];
    titleRef?: React.Ref<HTMLHeadingElement>;
    onSelect: (id: string) => void;
    onClose: () => void;
}) {
    const Body = BODY[node.icon];
    return (
        <>
            <InspectorHead kicker={KICKER[node.icon]} title={node.name} subtitle={node.meta} titleRef={titleRef} onClose={onClose} />
            <InspectorAttention items={attention} />
            <Body node={node} nodes={nodes} readings={readings} onSelect={onSelect} />
            {node.id === "internet" && <InternetEvents nodes={nodes} readings={readings} />}
        </>
    );
}

/** A lane's inspector: its nodes as rows, each one selecting its node. */
export function LaneInspector({
    lane,
    nodes,
    titleRef,
    onSelect,
    onClose,
}: {
    lane: Lane;
    nodes: MapNode[];
    titleRef?: React.Ref<HTMLHeadingElement>;
    onSelect: (id: string) => void;
    onClose: () => void;
}) {
    const inLane = nodes.filter((n) => n.lane === lane);
    return (
        <>
            <InspectorHead
                kicker={LANE_LABEL[lane]}
                title={inLane.length === 1 ? "1 connection" : `${inLane.length} connections`}
                subtitle={lane === "public" ? "What reaches this firewall from the internet" : "Your datacenters and cloud, over private links"}
                titleRef={titleRef}
                onClose={onClose}
            />
            {inLane.length === 0 ? (
                <p className="text-sm text-semantic-text-secondary">Nothing connected on this side.</p>
            ) : (
                <InspectorRows>
                    {inLane.map((node) => (
                        <InspectorRow
                            key={node.id}
                            title={node.name}
                            detail={node.meta}
                            aside={node.alert ? <GenericTag {...SEVERITY_TAG[node.alert]} size="small" /> : undefined}
                            onClick={() => onSelect(node.id)}
                        />
                    ))}
                </InspectorRows>
            )}
        </>
    );
}
